// useDNAProfile — Phase F read hook for the STRIKZA DNA Intelligence
// System. Calls the SECURITY DEFINER RPC `get_dna_profile_for_fixture`
// for one fixture_id and returns the active profile names + the
// per-profile bundle.
//
// Doctrine:
//   - Anon-friendly. The RPC itself is granted to anon for the
//     Phase 1A free-tier surfaces; tighten later if needed.
//   - Never fabricate. When a profile's row is absent or
//     sample_valid=false, the RPC omits its name from active_profiles
//     and the consuming UI must render nothing for it.

import { useMemo } from "react";
import { useRpcQuery } from "../data/useRpcQuery.ts";
import type { DNAProfileName } from "./dna-relevance.ts";

export interface DNAProfileBundle {
  /** Names of the profiles whose row was found AND sample_valid=true. */
  readonly activeProfiles: readonly DNAProfileName[];
  /** Raw per-profile bundle keyed by profile name. Shape varies. */
  readonly profiles: Readonly<Record<string, unknown>>;
  /** True only when the fixture itself was found in canonical_fixtures. */
  readonly fixtureFound: boolean;
}

export interface UseDNAProfileState {
  readonly bundle: DNAProfileBundle | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly refetch: () => Promise<void>;
}

function normaliseActive(raw: unknown): readonly DNAProfileName[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((x): x is DNAProfileName => typeof x === "string") as readonly DNAProfileName[];
}

export function useDNAProfile(fixtureId: string | null | undefined): UseDNAProfileState {
  const q = useRpcQuery<Record<string, unknown> | null>("get_dna_profile_for_fixture", {
    args: { p_fixture_id: fixtureId },
    enabled: Boolean(fixtureId),
  });

  const bundle = useMemo<DNAProfileBundle | null>(() => {
    if (!fixtureId) return null;
    const data = q.data;
    if (!data || typeof data !== "object") {
      return { activeProfiles: [], profiles: {}, fixtureFound: false };
    }
    const row = data as Record<string, unknown>;
    return {
      activeProfiles: normaliseActive(row.active_profiles),
      profiles:
        row.profiles && typeof row.profiles === "object"
          ? (row.profiles as Record<string, unknown>)
          : {},
      fixtureFound: row.fixture_found === true,
    };
  }, [fixtureId, q.data]);

  return useMemo<UseDNAProfileState>(
    () => ({
      bundle,
      loading: q.isLoading,
      error: q.error ? q.error.message : null,
      refetch: async () => {
        await q.refetch();
      },
    }),
    [bundle, q.isLoading, q.error, q.refetch],
  );
}
